import classNames from "classnames/dedupe";
import { useMemo } from "react";
import {
  useCommercialLandlordRedirect,
  useQuestionnaireInitialization,
  useSections,
} from "../../../features/questionnaire";
import { useEnvironmentValues } from "../../../hooks/environment/useEnvironment";
import type { WithMetadata } from "../../../pages";
import { Section, type FlattenedSectionProps } from "../Section";
import { QuestionnaireForm } from "../QuestionnaireForm";
import { useLocale } from "../../../hooks/useLocale";
import type { QuestionnaireProps } from "..";
import { Preamble, Postamble } from "../Amble";

/**
 * Stacked sections questionnaire variant.
 * Renders all sections at once in a single page without section navigation.
 */
export const WELCOME_PAGE_ID = "welcome";

export const StackedSectionsQuestionnaire = ({
  ref,
  ...props
}: WithMetadata<QuestionnaireProps>) => {
  const {
    elementType: Element = "div",
    config,
    sections,
    validations,
    componentName,
    actions,
    pageId,
    variant,
    preamble,
    preambleName,
    postamble,
    postambleName,
    ...otherProps
  } = props;
  const { locale } = useLocale();
  const { site } = useEnvironmentValues();

  // Include locale and variant in config object
  const configWithLocale = useMemo(
    () => ({
      ...config,
      locale,
      variant,
    }),
    [config, locale, variant],
  );
  const { sections: hydratedSections } = useQuestionnaireInitialization(
    sections,
    validations,
    configWithLocale,
  );
  useCommercialLandlordRedirect();

  // Atom sections → pre-computed initial sections (hydration fallback)
  const atomSections = useSections() as FlattenedSectionProps[];
  let flattenedSections = hydratedSections as FlattenedSectionProps[];
  if (atomSections.length) {
    flattenedSections = atomSections;
  }

  const submitActionLabel = useMemo(() => {
    const submitAction = actions?.find(
      action => action.actionType === "submit",
    );
    return submitAction?.label || "Continue to quotes";
  }, [actions]);

  const backAction = useMemo(() => {
    const foundBackAction = actions?.find(
      action => action.actionType === "back",
    );
    return foundBackAction;
  }, [actions]);

  const isWelcomePage = pageId === WELCOME_PAGE_ID;

  const classes = classNames(
    "mobius",
    "mobius-questionnaire",
    "mobius-questionnaire--stacked-sections",
    isWelcomePage && "mobius-questionnaire--welcome",
    otherProps.className,
  );

  const postambleNode = (
    <Postamble
      amble={postamble}
      ambleName={postambleName}
      site={site}
      locale={locale}
    />
  );

  return (
    <Element
      ref={ref}
      {...otherProps}
      {...(componentName ? { "data-component": componentName } : {})}
      {...(variant ? { "data-variant": variant } : {})}
      className={classes}
    >
      <Preamble
        amble={preamble}
        ambleName={preambleName}
        site={site}
        locale={locale}
      />
      {flattenedSections.length > 0 && (
        <>
          <QuestionnaireForm
            submitActionLabel={submitActionLabel}
            backAction={backAction}
            sections={sections}
            questionnaireVariant={variant}
          >
            {flattenedSections.map((section, index) => (
              <Section
                key={section.id || index}
                {...section}
                submitActionLabel={submitActionLabel}
                className={classNames(
                  section.className,
                  "mobius-section--stacked",
                )}
                variant={variant}
              />
            ))}
            {!isWelcomePage && postambleNode}
          </QuestionnaireForm>
          {isWelcomePage && postambleNode}
        </>
      )}
    </Element>
  );
};

StackedSectionsQuestionnaire.displayName = "StackedSectionsQuestionnaire";
